import React, { FunctionComponent, useCallback, useMemo, useState } from "react";
import {
  Intervention,
  InterventionGroup,
  LessonNotes,
  StudentGoal,
} from "../../../../../../../../../store/onboarding/cases/types";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faExclamationTriangle,
  faPen,
  faTrashAlt,
  faUserAlt,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { deleteLessonNotes } from "../../../../../../../../../store/onboarding/cases/actions";
import { showConfirmDialog } from "../../../../../../../../../store/confirm-dialog/actions";
import { getFullName } from "../../../../../../../../../utils/NamesUtils";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ApplicationState } from "../../../../../../../../../store";
import { UserInfo } from "../../../../../../../../../store/auth/types";
import AttachedResourceItem from "../../../../../explore-and-learn/intervention-library/new-intervention-modal/intervention-steps-form/AttachedResource";
import useInterventionGroupAccess from "../../../../../../../../../utils/hooks/useInterventionGroupAccess";

type Props = {
  notes: LessonNotes;
  group: InterventionGroup;
  interventions: Intervention[];
  archivedInterventions: Intervention[];
  onEdit: () => void;
  isStudentDetail?: boolean;
  isGroupDetail?: boolean;
};

const MAX_LENGTH = 140;

const LessonNotesItem: FunctionComponent<Props> = (props) => {
  const { notes, group, interventions, onEdit, isStudentDetail, isGroupDetail, archivedInterventions } = props;

  const [expanded, setExpanded] = useState(false);

  const isLongNote: boolean = useMemo(
    () => notes.notes.length > MAX_LENGTH,
    [notes.notes.length]);

  const displayNote: string = useMemo(
    () => expanded
      ? notes.notes
      : notes.notes.slice(0, MAX_LENGTH),
    [expanded, notes.notes]);

  const dispatch = useDispatch();

  const { isIntGrpIDsWithReadAccess } = useInterventionGroupAccess(group.id);

  const details = useMemo(() => {
    const interventionNames = notes.interventions.map(
      (intId) => {
        const interventionName = interventions.find((int) => int.id === intId)?.name;
        if (interventionName) {
          return interventionName;
        } else {
          return archivedInterventions.find((int) => int.id === intId)?.name;
        }
      }
    );

    return (notes.is_general
      ? ["General Notes", ...interventionNames]
      : interventionNames
    ).join(", ");
  }, [notes.is_general, notes.interventions, interventions]);

  const onRemove = useCallback(() => {
    if (group.id && notes.id) {
      dispatch(deleteLessonNotes(group.id, notes.id));
    }
  }, [group.id, notes.id]);

  const handleDelete = useCallback(() => {
    dispatch(
      showConfirmDialog({
        onConfirm: onRemove,
        title: "Delete Note",
        text: (
          <>
            <p className="font-weight-bold"> Are you sure you want to delete this note?</p>
            <p className="text-center">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                style={{ color: 'red' }}
              /> {" "}
              <i>This action cannot be undone.</i>
            </p>
          </>
        ),
        confirmButtonText: "DELETE",
        centered: true,
      })
    );
  }, [onRemove]);

  const myId = useSelector((s: ApplicationState) => s.auth.userInfo?.id);

  const sharedWith: Array<string> | undefined = useMemo(() => {
    const users: UserInfo[] = [
      group.coach_assignment?.user,
      group.teacher_assignment?.user,
      ...group.associated_assignments.map((aa) => aa.user),
    ].filter<UserInfo>((user): user is UserInfo => {
      return !!user && notes.viewable_by.includes(user.id);
    });

    if (!users.length) {
      return undefined;
    }

    if (users.length === 1 && users[0].id === myId) {
      return ["Me"];
    } else {
      const getNameOrMe = (user: UserInfo) => {
        if (user.id === myId) {
          return "Me";
        } else {
          return getFullName(user);
        }
      };
      return users.map(getNameOrMe);
    }
  }, [notes.viewable_by, myId]);

  const filterStudents = (
      selectedInterventionGroup: InterventionGroup | undefined
    ) => {
      const students = selectedInterventionGroup?.students.filter(
        (student, index) => {
          let studentGoal: StudentGoal | undefined;
          if (selectedInterventionGroup?.student_goals) {
            studentGoal = selectedInterventionGroup?.student_goals.find(
              (x) => x.student.id === student.id && !x.archived
            );
          }
          return !!studentGoal;
        }
      );
      return students;
    };

    const filteredStudents = useMemo(() => filterStudents(group), [group]);

  return (
    <div className="lesson-notes-item p-3">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3">

        <div className="d-flex">
          <div className="label-col font-weight-bold">Date:</div>
          <div>{moment(notes.date).format("MM/DD/YYYY")}</div>
        </div>

        <div className="d-flex align-items-center">

          {sharedWith?.length &&
            !isStudentDetail &&
            !isGroupDetail &&
            !isIntGrpIDsWithReadAccess && (
              <OverlayTrigger
                overlay={
                  <Tooltip id={`tooltip-${notes.id}`}>
                    <strong>Shared with: </strong>
                    {sharedWith.join(", ")}
                  </Tooltip>
                }
              >
                <span>
                  <FontAwesomeIcon
                    icon={sharedWith.length < 2 ? faUserAlt : faUsers}
                    className="default-pointer"
                  />
                </span>
              </OverlayTrigger>
            )}

          {!isStudentDetail &&
            !isGroupDetail &&
            !isIntGrpIDsWithReadAccess && (
              <>
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip id={`edit-tooltip-${notes.id}`} className="customTooltip">
                      Edit
                    </Tooltip>
                  }
                >
                  <span className="pointer ml-2">
                    <FontAwesomeIcon icon={faPen} onClick={onEdit} />
                  </span>
                </OverlayTrigger>

                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip id={`delete-tooltip-${notes.id}`} className="customTooltip">
                      Delete
                    </Tooltip>
                  }
                >
                  <span className="pointer ml-2" onClick={handleDelete}>
                    <FontAwesomeIcon
                      icon={faTrashAlt}
                      style={{ color: "red" }}
                    />
                  </span>
                </OverlayTrigger>
              </>
            )}
        </div>
      </div>

      {/* Intervention */}
      <div className="info-row">
        <div className="label-col font-weight-bold">
          Intervention(s):
        </div>

        <div className="content-col">
          {details || <span className="text-muted">No Data Added</span>}
        </div>
      </div>

      {/* Records */}
      <div className="info-row">
        <div className="label-col font-weight-bold">
          Records & Next Steps:
        </div>

        <div className="content-col">
          {notes.record_tags?.length > 0 ? (
            <div className="tagsContainer">
              {notes.record_tags
                .sort((a, b) => a.order - b.order)
                .map((tag, index) => (
                  <div
                    key={`tag_${index}`}
                    className="tagCell"
                  >
                    {tag.text}
                  </div>
                ))}
            </div>
          ) : (
            <span className="text-muted">No Data Added</span>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="info-row">
        <div className="label-col font-weight-bold">
          {getFullName(notes.user)}:
        </div>

        <div className="content-col">
          {displayNote ? displayNote : <span className="text-muted">No Data Added</span>}

          {!expanded && isLongNote && (
            <span>
              ...{" "}
              <span
                className="text-primary pointer"
                onClick={() => setExpanded(true)}
              >
                Show More
              </span>
            </span>
          )}

          {expanded && isLongNote && (
            <span>
              {" "}
              <span
                className="text-primary pointer"
                onClick={() => setExpanded(false)}
              >
                Show Less
              </span>
            </span>
          )}
        </div>
      </div>

      <div className="section-divider" />

      {/* Tagged Students */}
      <div className="info-row">
        <div className="label-col font-weight-bold">
          Tagged Students:
        </div>

        <div className="content-col">
          {(notes.pertaining_students?.length ?? 0) > 0 ? (
            <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip id={`edit-tooltip-${notes.id}`} className="customTooltip">
                      {filteredStudents?.filter((student) => notes.pertaining_students?.includes(student.id!)).map((student) => getFullName(student)).join(", ")}
                    </Tooltip>
                  }
                >
                  <span className="pointer ml-2">
                    <FontAwesomeIcon icon={faUsers} />
                  </span>
                </OverlayTrigger>
            
          ) : (
            <span className="text-muted">No Student(s) Tagged</span>
          )}
        </div>
      </div>

      <div className="section-divider" />

      {/* Resources */}
      <div className="info-row">
        <div className="label-col font-weight-bold">
          Resources:
        </div>

        <div className="content-col">
          {notes.attachments?.length ? (
            notes.attachments.map((attachment) => (
              <AttachedResourceItem
                attachment={attachment}
                key={attachment.id}
              />
            ))
          ) : (
            <span className="text-muted">No Data Added</span>
          )}
        </div>
      </div>

    </div>
  );
};

export default LessonNotesItem;
